import { Purchases, LOG_LEVEL } from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';

const REVENUECAT_API_KEY = 'appl_KOjqBVoxUEeXaaEwfeYblsPAKhx';
const ENTITLEMENT_ID = 'Babybookin Pro';

let isConfigured = false;

/**
 * Initialize RevenueCat. 
 * - Called WITHOUT userId on app launch (anonymous mode — enables pre-login purchases).
 * - Called WITH userId after Firebase login (identifies the user and merges any anonymous purchases).
 */
export const initializeRevenueCat = async (userId) => {
  if (!Capacitor.isNativePlatform()) {
    console.log('RevenueCat: Not on native platform, skipping initialization');
    return;
  }

  try {
    await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });

    if (!isConfigured) {
      // First launch — configure anonymously (no appUserID).
      // RevenueCat generates its own anonymous ID internally.
      await Purchases.configure({
        apiKey: REVENUECAT_API_KEY,
      });
      isConfigured = true;
      console.log('RevenueCat configured (anonymous mode)');
    }

    // If we have a Firebase userId, identify (log in) so purchases
    // made anonymously get merged into this account.
    if (userId) {
      try {
        await Purchases.logIn({ appUserID: userId });
        console.log('RevenueCat identified user:', userId);
      } catch (loginErr) {
        console.warn('RevenueCat logIn warning (non-fatal):', loginErr);
      }
    }
  } catch (error) {
    console.error('RevenueCat initialization error:', error);
  }
};

/**
 * Ensure RevenueCat is configured before any purchase/restore/check.
 * Handles the case where the user taps "Subscribe" on the login screen
 * before onAuthStateChanged has fired.
 */
const ensureConfigured = async () => {
  if (!Capacitor.isNativePlatform()) return false;

  if (!isConfigured) {
    try {
      await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
      await Purchases.configure({
        apiKey: REVENUECAT_API_KEY,
      });
      isConfigured = true;
      console.log('RevenueCat configured on-demand (anonymous)');
    } catch (err) {
      console.error('RevenueCat on-demand configure failed:', err);
      return false;
    }
  }
  return true;
};

export const checkPremiumStatus = async () => {
  if (!Capacitor.isNativePlatform()) {
    return null; // Return null on web so we use Firebase status
  }

  const ready = await ensureConfigured();
  if (!ready) return null;

  try {
    const customerInfo = await Purchases.getCustomerInfo();
    const isPremium = customerInfo.customerInfo.entitlements.active[ENTITLEMENT_ID] !== undefined;
    return isPremium;
  } catch (error) {
    console.error('Error checking premium status:', error);
    return null;
  }
};

export const getOfferings = async () => {
  if (!Capacitor.isNativePlatform()) {
    return null;
  }

  const ready = await ensureConfigured();
  if (!ready) return null;

  try {
    const offerings = await Purchases.getOfferings();
    return offerings.current;
  } catch (error) {
    console.error('Error getting offerings:', error);
    return null;
  }
};

export const purchasePremium = async () => {
  if (!Capacitor.isNativePlatform()) {
    alert('Subscriptions are only available in the iOS app. Please download from the App Store to subscribe.');
    return { success: false };
  }

  const ready = await ensureConfigured();
  if (!ready) {
    alert('Unable to connect to the subscription service. Please try again.');
    return { success: false };
  }

  try {
    const offerings = await Purchases.getOfferings();

    if (!offerings.current || !offerings.current.availablePackages.length) {
      alert('No subscription packages available. Please try again later.');
      return { success: false };
    }

    // Get the monthly package
    const packageToPurchase = offerings.current.availablePackages.find(
      pkg => pkg.packageType === 'MONTHLY'
    ) || offerings.current.availablePackages[0];

    const purchaseResult = await Purchases.purchasePackage({
      aPackage: packageToPurchase,
    });

    const isPremium = purchaseResult.customerInfo.entitlements.active[ENTITLEMENT_ID] !== undefined;

    return { success: isPremium, customerInfo: purchaseResult.customerInfo };
  } catch (error) {
    if (error.code === '1' || error.message?.includes('cancelled')) {
      // User cancelled
      return { success: false, cancelled: true };
    }
    console.error('Purchase error:', error);
    alert('Purchase failed. Please try again.');
    return { success: false, error };
  }
};

export const restorePurchases = async () => {
  if (!Capacitor.isNativePlatform()) {
    alert('Restore is only available in the iOS app.');
    return { success: false };
  }

  const ready = await ensureConfigured();
  if (!ready) {
    alert('Unable to connect to the subscription service. Please try again.');
    return { success: false };
  }

  try {
    const customerInfo = await Purchases.restorePurchases();
    const isPremium = customerInfo.customerInfo.entitlements.active[ENTITLEMENT_ID] !== undefined;
    return { success: true, isPremium, customerInfo: customerInfo.customerInfo };
  } catch (error) {
    console.error('Restore error:', error);
    alert('Could not restore purchases. Please try again.');
    return { success: false, error };
  }
};